/**
 * Área FICHA · DOSSIÊ DA CLIENTE: as seis leituras que a prévia do dossiê junta.
 *
 * O legado lê as seis em sequência no clique de "Gerar prévia" (`admin.html:5585` a `5590`), e a
 * Leitura Estratégica lê as duas primeiras do mesmo jeito para calcular o radar (`6298` e `6299`).
 * A gravação da prévia é um envio ao Storage (`5610`), e a lista das prévias é uma listagem
 * (`5571` e `5573`), as duas em `ficha-arquivos.ts`.
 *
 * O LEGADO IGNORA A FALHA destas leituras (`const { data: geral } = ...`): leitura que falha vira
 * seção "ainda não respondeu" no documento. A tela nova faz igual, e por isso quem decide o que
 * fazer com `ok: false` é ela, e não este módulo.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  comEscopo,
  executar,
  type Escopo,
  type FonteDeDados,
  type LinhaChecklistAtendimento,
  type LinhaChecklistColoracao,
  type LinhaChecklistMilhoes,
  type LinhaQuestionarioAvaliativo,
  type LinhaQuestionarioGeral,
  type LinhaQuestionarioPersonalidade,
  type Resultado,
} from '../nucleo.js';

/**
 * A leitura comum às seis: `comEscopo(sb.from(tabela).select('*')).maybeSingle()`.
 * Uma função só porque as seis consultas do legado são literalmente a mesma, trocando a tabela.
 */
function lerLinhaDaFicha<T>(fonte: FonteDeDados, escopo: Escopo, tabela: string): Promise<Resultado<T | null>> {
  // `maybeSingle` porque a cliente pode ainda não ter respondido: ausência é `null`, e não erro.
  return executar<T | null>(comEscopo(clienteDe(fonte).from(tabela).select('*'), escopo).maybeSingle());
}

/** Questionário Geral. Legado: `admin.html:5585` e `6298`. */
export function lerQuestionarioGeral(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<LinhaQuestionarioGeral | null>> {
  return lerLinhaDaFicha<LinhaQuestionarioGeral>(fonte, escopo, 'questionario_geral');
}

/** Questionário de Personalidade. Legado: `admin.html:5586` e `6299`. */
export function lerQuestionarioPersonalidade(
  fonte: FonteDeDados,
  escopo: Escopo,
): Promise<Resultado<LinhaQuestionarioPersonalidade | null>> {
  return lerLinhaDaFicha<LinhaQuestionarioPersonalidade>(fonte, escopo, 'questionario_personalidade');
}

/** Checklist de Atendimento. Legado: `admin.html:5587`. */
export function lerChecklistAtendimento(
  fonte: FonteDeDados,
  escopo: Escopo,
): Promise<Resultado<LinhaChecklistAtendimento | null>> {
  return lerLinhaDaFicha<LinhaChecklistAtendimento>(fonte, escopo, 'checklist_atendimento');
}

/** Checklist de Milhões. Legado: `admin.html:5588`. */
export function lerChecklistMilhoes(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<LinhaChecklistMilhoes | null>> {
  return lerLinhaDaFicha<LinhaChecklistMilhoes>(fonte, escopo, 'checklist_milhoes');
}

/** Questionário Avaliativo. Legado: `admin.html:5589`. */
export function lerQuestionarioAvaliativo(
  fonte: FonteDeDados,
  escopo: Escopo,
): Promise<Resultado<LinhaQuestionarioAvaliativo | null>> {
  return lerLinhaDaFicha<LinhaQuestionarioAvaliativo>(fonte, escopo, 'questionario_avaliativo');
}

/** Coloração, ficha de atendimento. Legado: `admin.html:5590`. */
export function lerChecklistColoracao(
  fonte: FonteDeDados,
  escopo: Escopo,
): Promise<Resultado<LinhaChecklistColoracao | null>> {
  return lerLinhaDaFicha<LinhaChecklistColoracao>(fonte, escopo, 'checklist_coloracao');
}

/**
 * O tipo do arquivo da prévia: documento do Word (`admin.html:5609` e `5610`). O legado grava HTML
 * com esse tipo para o Word abrir o arquivo `.doc` direto, sem conversão.
 */
export const TIPO_PREVIA_DOSSIE = 'application/msword';
